import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { companyApi } from '../../api/company.api';
import { userApi } from '../../api/user.api';
import { PageHeader, Btn, EmptyState, PasswordField } from '../../components/ui/index';
import { useUI } from '../../store/UIContext';
import { useAuth } from '../../store/AuthContext';
import { phoneError, normalizeIranianMobile, phoneInputChange, innerPhoneError, innerPhoneInputChange } from '../../utils/validators';
import { composeCompanyUsername, DEFAULT_USERNAME_SPLITTER } from '../../utils/usernamePrefix';

// Field names/shapes here must match the backend exactly:
// - queryAddCompanyWithNewUser needs company_name, description, contract_type,
//   inner_phone_number, username, password, first_name, last_name (+ optional
//   image_data) - sent as a JSON body (views.addCompanyWithNewUser does
//   json.loads(request.body), not multipart form data).
// - addCompanyWithExistUser reads GET params name, description, manager_name
//   (an existing user's *username*, not a numeric id).
export default function AddCompanyPage() {
  const navigate = useNavigate();
  const { addToast } = useUI();
  const { user } = useAuth() || {};
  const [mode, setMode] = useState('new'); // 'new' | 'exist'
  const [users, setUsers] = useState([]);
  const [selectedUser, setSelectedUser] = useState('');
  const [form, setForm] = useState({
    name: '', displayName: '', description: '', contractType: '', innerPhoneNumber: '',
    username: '', password: '', password2: '', firstName: '', lastName: '', phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [splitter, setSplitter] = useState(DEFAULT_USERNAME_SPLITTER);

  useEffect(() => {
    userApi.getUserList().then(r => setUsers(Array.isArray(r.data?.user_list) ? r.data.user_list : [])).catch(() => {});
    userApi.getUsernamePrefixSettings()
      .then((res) => setSplitter(res.data?.splitter || DEFAULT_USERNAME_SPLITTER))
      .catch(() => {});
  }, []);

  const set = k => e => setForm(p => ({ ...p, [k]: e.target.value }));

  const submit = async () => {
    if (!form.name.trim()) { addToast('نام شرکت الزامی است', 'error'); return; }
    if (mode === 'exist' && !selectedUser) { addToast('یک کاربر انتخاب کنید', 'error'); return; }
    if (mode === 'new' && form.password !== form.password2) {
      addToast('تکرار رمز عبور با رمز عبور یکسان نیست', 'error');
      return;
    }
    if (mode === 'new' && (!form.username.trim() || !form.password.trim())) {
      addToast('نام کاربری و رمز عبور الزامی است', 'error'); return;
    }
    if (mode === 'new' && (!form.phone.trim() || phoneError(form.phone))) {
      addToast('شماره موبایل معتبر الزامی است', 'error'); return;
    }
    if (mode === 'new' && innerPhoneError(form.innerPhoneNumber)) {
      addToast(innerPhoneError(form.innerPhoneNumber), 'error'); return;
    }
    setLoading(true);
    try {
      if (mode === 'exist') {
        await companyApi.addCompanyWithExistUser({ name: form.name, display_name: form.displayName.trim(), description: form.description, manager_name: selectedUser });
      } else {
        await companyApi.addCompanyWithNewUser({
          name: form.name,
          display_name: form.displayName.trim(),
          description: form.description,
          contract_type: form.contractType,
          inner_phone_number: form.innerPhoneNumber,
          username: form.username,
          password: form.password,
          first_name: form.firstName,
          last_name: form.lastName,
          phone_number: normalizeIranianMobile(form.phone),
          image_data: '',
        });
      }
      addToast('شرکت با موفقیت افزوده شد', 'success');
      navigate('/companies');
    } catch (err) {
      const msg = err?.response?.data;
      addToast(typeof msg === 'string' && msg.trim() ? msg : 'خطا در افزودن شرکت', 'error');
    }
    setLoading(false);
  };

  const inp = { padding:'10px 14px', border:'1.5px solid #e2e8f0', borderRadius:8, fontSize:13, outline:'none', fontFamily:'inherit', width:'100%', boxSizing:'border-box' };
  const label = { fontSize:12, fontWeight:600, color:'#334155', display:'block', marginBottom:5 };

  // Only dispatcher always has this; a manager_employee/simple_employee
  // needs can_manage_companies granted by the dispatcher (or delegated by
  // their manager) - see ManagerCompanyPermissionsPage/ManageCompanyDelegationPage.
  // Backend enforces this too (addCompanyWithNewUser/addCompanyWithExistUser),
  // this is just so the form doesn't render for someone who'd get a 403 anyway.
  if (user?.type !== 'dispatcher' && user?.type !== 'admin' && !user?.can_manage_companies) {
    return (
      <div className="fade-in">
        <PageHeader title="افزودن شرکت جدید" />
        <EmptyState title="دسترسی ندارید" subtitle="برای تعریف شرکت جدید باید این اجازه از سوی دیسپچر یا مدیر دپارتمان به شما داده شده باشد." />
      </div>
    );
  }

  return (
    <div className="fade-in">
      <PageHeader title="افزودن شرکت جدید" />
      <div style={{maxWidth:560, background:'#fff', borderRadius:16, border:'1px solid rgba(35,58,122,0.1)', padding:32, boxShadow:'0 4px 16px rgba(35,58,122,0.08)'}}>
        <div style={{display:'flex',gap:8,marginBottom:24}}>
          {['new','exist'].map(m=>(
            <button key={m} onClick={()=>setMode(m)} style={{flex:1,padding:'9px',borderRadius:8,border:`1.5px solid ${mode===m?'#29AAE1':'#e2e8f0'}`,background:mode===m?'#EDF3FC':'#fff',color:mode===m?'#233A7A':'#64748b',fontWeight:700,fontSize:13,cursor:'pointer',fontFamily:'inherit'}}>
              {m==='new'?'کاربر جدید':'کاربر موجود'}
            </button>
          ))}
        </div>
        <div style={{display:'flex',flexDirection:'column',gap:14}}>
          <div><label style={label}>نام شرکت (پیشوند نام کاربری) *</label><input value={form.name} onChange={set('name')} placeholder="نام شرکت" style={inp}/></div>
          <div><label style={label}>نام نمایشی</label><input value={form.displayName} onChange={set('displayName')} placeholder="نامی که به مسئولان نمایش داده می‌شود (اختیاری)" style={inp}/></div>
          <div><label style={label}>توضیحات</label><input value={form.description} onChange={set('description')} placeholder="توضیحات شرکت" style={inp}/></div>
          {mode==='exist'?(
            <div>
              <label style={label}>انتخاب کاربر موجود</label>
              <select value={selectedUser} onChange={e=>setSelectedUser(e.target.value)} style={{...inp,cursor:'pointer'}}>
                <option value="">-- انتخاب کاربر --</option>
                {users.map(u=><option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          ):(
            <>
              <div><label style={label}>نوع قرارداد</label><input value={form.contractType} onChange={set('contractType')} placeholder="مثال: سالانه" style={inp}/></div>
              <div>
                <label style={label}>تلفن داخلی</label>
                <input value={form.innerPhoneNumber} onChange={innerPhoneInputChange(set('innerPhoneNumber'))} placeholder="مثال: 101" inputMode="numeric" maxLength={5} dir="ltr" style={inp}/>
                <p style={{ fontSize: 11, color: '#94a3b8', margin: '4px 0 0' }}>اختیاری — extension کوتاه؛ شماره موبایل را در فیلد پایین وارد کنید</p>
              </div>
              <div><label style={label}>نام</label><input value={form.firstName} onChange={set('firstName')} placeholder="نام صاحب شرکت" style={inp}/></div>
              <div><label style={label}>نام خانوادگی</label><input value={form.lastName} onChange={set('lastName')} placeholder="نام خانوادگی صاحب شرکت" style={inp}/></div>
              <div>
                <label style={label}>نام کاربری *</label>
                <input value={form.username} onChange={set('username')} style={inp}/>
                {form.username.trim() && form.name.trim() && (
                  <p dir="ltr" style={{fontSize:12,color:'#64748b',margin:'6px 0 0',wordBreak:'break-all'}}>
                    نام کاربری نهایی: {composeCompanyUsername(form.name, form.username, splitter)}
                  </p>
                )}
              </div>
              <PasswordField label="رمز عبور *" labelStyle={label} value={form.password} onChange={set('password')} />
              <PasswordField label="تکرار رمز عبور *" labelStyle={label} value={form.password2} onChange={set('password2')} error={form.password2 && form.password !== form.password2 ? 'تکرار رمز عبور با رمز عبور یکسان نیست' : ''} />
              <div><label style={label}>شماره موبایل *</label><input type="tel" inputMode="numeric" maxLength={11} value={form.phone} onChange={phoneInputChange(set('phone'))} placeholder="09xxxxxxxxx" dir="ltr" style={inp}/></div>
            </>
          )}
          <div style={{display:'flex',gap:10,marginTop:8}}>
            <Btn variant="ghost" onClick={()=>navigate('/companies')}>انصراف</Btn>
            <Btn onClick={submit} loading={loading}>ذخیره</Btn>
          </div>
        </div>
      </div>
    </div>
  );
}
